import React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ArrowLeftIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useAdminSplaUsageDetail } from '../../../hooks/useAdminSplaUsage';
import { formatMonthYear, formatWallClockDateTime } from '../../../utils/dateFormat';
import { formatUnitPrice, trimDecimalZeros } from '../../../utils/money';
import { SplaSubjectCell } from '../../utils/splaSubjects';
import type { SplaUsageRow } from '../../../types/models/Spla';
import { ReportStatusLabel } from './SplaUsageTab';

interface SplaUsageMonthDetailProps {
  /** Defers the fetch until the page's staff check has passed. */
  isStaff: boolean;
  reportId: number;
  onBack: () => void;
}

/**
 * One report month, drilled into: the report header, the WORKBOOK PREVIEW (the exact line-set the
 * mailed sheet renders — aggregated by part number across all subjects and clients, totalled on the
 * snapshot distributor price), and the frozen per-subject rows (the audit detail). Everything is a
 * snapshot string rendered verbatim — no total is computed client-side, and nothing here can be
 * edited or deleted.
 */
function SplaUsageMonthDetail({ isStaff, reportId, onBack }: SplaUsageMonthDetailProps) {
  const { detail, loading, error, refetch } = useAdminSplaUsageDetail(reportId, isStaff);

  const backButton = (
    <Button variant="link" isInline icon={<ArrowLeftIcon />} onClick={onBack} className="pf-v6-u-mb-md">
      Back to all months
    </Button>
  );

  if (loading || (!detail && !error)) {
    return (
      <>
        {backButton}
        <CenteredSpinner />
      </>
    );
  }

  if (error || !detail) {
    return (
      <>
        {backButton}
        <EmptyState
          titleText="Unable to load the report"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error ?? 'The report could not be loaded.'}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </>
    );
  }

  const { report, aggregate, rows } = detail;

  const renderSalePrice = (row: SplaUsageRow) => {
    if (row.sale_unit_price === null) {
      return (
        <Tooltip content="No sale price had resolved at snapshot time — the workbook is unaffected, invoicing will fail loud.">
          <Label color="orange" isCompact>
            Unpriced
          </Label>
        </Tooltip>
      );
    }
    return (
      <>
        {formatUnitPrice(row.sale_unit_price)}
        {row.sale_is_override === true && (
          <>
            {' '}
            <Label color="yellow" isCompact>
              override
            </Label>
          </>
        )}
      </>
    );
  };

  return (
    <>
      {backButton}
      <Card className="pf-v6-u-mb-md">
        <CardTitle>
          Report — {formatMonthYear(report.month)} <ReportStatusLabel report={report} />
        </CardTitle>
        <CardBody>
          <DescriptionList isHorizontal isCompact columnModifier={{ default: '2Col' }}>
            <DescriptionListGroup>
              <DescriptionListTerm>Generations</DescriptionListTerm>
              <DescriptionListDescription>generated {report.generation}×</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Rows</DescriptionListTerm>
              <DescriptionListDescription>{report.row_count}</DescriptionListDescription>
            </DescriptionListGroup>
            {report.frozen && (
              <>
                <DescriptionListGroup>
                  <DescriptionListTerm>Sent at</DescriptionListTerm>
                  <DescriptionListDescription>{formatWallClockDateTime(report.sent_at)}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Sent to</DescriptionListTerm>
                  <DescriptionListDescription>{report.sent_to ?? '—'}</DescriptionListDescription>
                </DescriptionListGroup>
              </>
            )}
            {report.file_path !== null && (
              <DescriptionListGroup>
                <DescriptionListTerm>Archived workbook</DescriptionListTerm>
                <DescriptionListDescription>
                  {report.file_path}
                  {report.file_sha256 !== null && (
                    <div>
                      <Content component="small">SHA-256: {report.file_sha256}</Content>
                    </div>
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
            )}
          </DescriptionList>
        </CardBody>
      </Card>

      <Content component="h2" className="pf-v6-u-mb-sm">
        Workbook preview
      </Content>
      <Content component="p" className="pf-v6-u-mb-sm">
        The exact line-set the mailed sheet renders — quantities summed across all subjects and clients,
        totalled on the snapshot distributor price.
      </Content>
      {aggregate.lines.length === 0 ? (
        <Content component="p" className="pf-v6-u-mb-md">
          <em>The snapshot is empty — a zero-usage month still gets reported.</em>
        </Content>
      ) : (
        <Table aria-label="Workbook preview lines" variant="compact" className="pf-v6-u-mb-md">
          <Thead>
            <Tr>
              <Th>Category</Th>
              <Th>Part number</Th>
              <Th>Item name</Th>
              <Th>Product family</Th>
              <Th>Agreement</Th>
              <Th>Unit price</Th>
              <Th>Quantity</Th>
              <Th>Subjects</Th>
              <Th>Clients</Th>
              <Th>Line total</Th>
            </Tr>
          </Thead>
          <Tbody>
            {aggregate.lines.map((line) => (
              <Tr key={`${line.spla_license_id}`}>
                <Td dataLabel="Category">{line.category_name}</Td>
                <Td dataLabel="Part number">{line.part_number}</Td>
                <Td dataLabel="Item name">
                  <Content component="small">{line.item_name}</Content>
                </Td>
                <Td dataLabel="Product family">{line.product_family}</Td>
                <Td dataLabel="Agreement">
                  {line.agreement_type}
                  {line.agreement_type === 'Academic' ? ` · ${line.offering}` : ''}
                </Td>
                <Td dataLabel="Unit price">{formatUnitPrice(line.distributor_unit_price)}</Td>
                <Td dataLabel="Quantity">{trimDecimalZeros(line.quantity)}</Td>
                <Td dataLabel="Subjects">{line.subject_count}</Td>
                <Td dataLabel="Clients">{line.client_count}</Td>
                <Td dataLabel="Line total">{formatUnitPrice(line.line_total)}</Td>
              </Tr>
            ))}
            <Tr>
              <Td colSpan={9}>
                <strong>Total</strong>
              </Td>
              <Td dataLabel="Total">
                <strong>{formatUnitPrice(aggregate.total)}</strong>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      )}

      <Content component="h2" className="pf-v6-u-mb-sm">
        Per-subject rows
      </Content>
      <Content component="p" className="pf-v6-u-mb-sm">
        The frozen audit detail behind the aggregate — every column snapshotted verbatim at generation
        time.
      </Content>
      {rows.length === 0 ? (
        <Content component="p">
          <em>No rows in this snapshot.</em>
        </Content>
      ) : (
        <Table aria-label="Frozen per-subject usage rows" variant="compact">
          <Thead>
            <Tr>
              <Th>Client</Th>
              <Th>License</Th>
              <Th>Subject</Th>
              <Th>Quantity</Th>
              <Th>Distributor price</Th>
              <Th>Sale price</Th>
              <Th>Source</Th>
              <Th>Note</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.id}>
                <Td dataLabel="Client">{row.client.name}</Td>
                <Td dataLabel="License">
                  <Content component="small">{row.license.category_name} · </Content>
                  {row.license.part_number}
                  <div>
                    <Content component="small">{row.license.product_family}</Content>
                  </div>
                </Td>
                <Td dataLabel="Subject">
                  <SplaSubjectCell type={row.subject.type} subjectRef={row.subject.ref} name={row.subject.name} />
                </Td>
                <Td dataLabel="Quantity">{trimDecimalZeros(row.quantity)}</Td>
                <Td dataLabel="Distributor price">{formatUnitPrice(row.distributor_unit_price)}</Td>
                <Td dataLabel="Sale price">{renderSalePrice(row)}</Td>
                <Td dataLabel="Source">{row.source}</Td>
                <Td dataLabel="Note">
                  {row.note !== null ? <Content component="small">{row.note}</Content> : ''}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </>
  );
}

export default SplaUsageMonthDetail;
